import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

import { LoginFormContainer } from "./styled/login";

import { createCompany, createCompanyInfo } from "../../../store/company";
import { handleLogin } from "../../../store/user";

import Message from "./message";

export default function LoginForm({ getSchedulerSetting, url = "", homepageInfo = null }) {
  const location = useLocation();
  const dispatch = useDispatch();

  const isMobile = useMediaQuery({ query: "(max-width:768px)" });

  const [loginInfo, setLoginInfo] = useState({ userId: "", password: "" });
  const [rememberId, setRememberId] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const noValue = urlParams.get("no");

    checkAndOpenApp(noValue);

    if (location.search) {
      const params = Object.fromEntries(urlParams.entries());
      sessionStorage.setItem("params", JSON.stringify(params));
    }

    const storedData = localStorage.getItem("monoti");
    if (storedData) {
      const decodedId = atob(JSON.parse(storedData));
      setLoginInfo((data) => ({ ...data, userId: decodedId }));
      setRememberId(true);
    }
  }, []);

  const checkAndOpenApp = async (noValue) => {
    if (noValue) {
      try {
        const openAppResult = await openApp(`?scheduleNo=${noValue}`);
        if (!openAppResult) {
          sessionStorage.setItem("sharedEvent", JSON.stringify(noValue));
        }
      } catch (error) {
        console.error("앱 실행 중 오류 발생:", error);
      }
    }
  };

  // 초대 받은 경우
  const handleInvite = async (mberNo) => {
    const params = JSON.parse(sessionStorage.getItem("params"));

    const url = "/api/notification";
    const body = {
      gbn: "I",
      invite: {
        gbn: params.gbn,
        mberNoList: [mberNo],
        orgNo: params.orgNo,
      },
    };

    for (const key in body.invite) {
      if (body.invite.hasOwnProperty(key) && !body.invite[key]) {
        console.log("object contains empty values");
        return;
      }
    }

    const res = await axios.post(url, body);
    if (res.data.body.success) {
      sessionStorage.removeItem("params");
    }
  };

  // 카카오 로그인 처리
  const loginWithKakao = () => {
    Kakao.Auth.login({
      success: function (authObj) {
        const token = authObj.access_token;
        axios
          .request({
            method: "POST",
            url: "https://kapi.kakao.com/v2/user/me",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + token,
              "Content-type": "application/x-www-form-urlencoded;charset=utf-8",
            },
          })
          .then(async (response) => {
            const { data, status } = response;
            if (status === 200) {
              await kakaoSignIn(data);
            }
          })
          .catch((e) => {
            console.error(e);
          });
      },
      fail: function (err) {
        alert("error!!!!" + JSON.stringify(err));
      },
    });
  };

  // 카카오 로그인 후 사용자 정보 처리
  const kakaoSignIn = async (user) => {
    const url = "/api/kakaoSignIn";
    const req = {
      email: user.kakao_account.email,
      id: user.id,
      name: user.kakao_account.name,
      phoneNumber: user.kakao_account.phone_number,
    };
    const res = await axios.post(url, req);
    if (!res?.data?.userName) {
      return false;
    } else {
      handlePostLogin(res.data);
    }
  };

  // 아이디 저장
  const handleSaveId = (id) => {
    if (rememberId && !isMobile) {
      const encodedId = btoa(id);
      localStorage.setItem("monoti", JSON.stringify(encodedId));
    } else {
      localStorage.removeItem("monoti");
    }
  };

  // 로그인
  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = "/api/signIn";
    const req = {
      id: loginInfo.userId,
      pwd: loginInfo.password,
    };

    if (!loginInfo.userId || !loginInfo.password) {
      toast.error("아이디 또는 비밀번호를 입력해주세요.");
      return;
    }

    if (localStorage.getItem("msgToken")) {
      req.fcmToken = localStorage.getItem("msgToken");
    }

    const res = await axios.post(url, req);

    if (res.data.success === false) {
      toast.error(res.data.message);
    } else {
      handlePostLogin(res.data);
    }
  };

  const getCompanys = async (mberNo) => {
    try {
      const url = "/api/mberCmpnys";
      const body = { mberNo };
      const res = await axios.post(url, body);

      if (res.status === 200) {
        const companies = await Promise.all(
          res.data.map(async (company) => {
            const isStore = await getStore(company.cmpnyNo);

            return {
              ...company,
              gbn: isStore ? "ss" : "",
            };
          }),
        );

        dispatch(createCompany(companies));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getStore = async (cmpnyNo) => {
    try {
      const url = "/erp/ss/company/get";
      const body = { cmpnyNo, isVisible: false };
      const res = await axios.post(url, body);

      return res?.data?.cmpnyNo || null;
    } catch (error) {
      return null;
    }
  };

  // 로그인 후 이벤트
  const handlePostLogin = async (member) => {
    localStorage.setItem("token", member.accessToken);

    dispatch(handleLogin(member));
    await getCompanys(member.mberNo);

    if (sessionStorage.getItem("params")) {
      await handleInvite(member.mberNo);
    }
    handleSaveId(loginInfo.userId);

    const scheduleNo = await getScheduleNo(JSON.parse(sessionStorage.getItem("sharedEvent")));
    if (scheduleNo && parseInt(scheduleNo)) {
      await getEvent(scheduleNo, member);
    }

    const couponNo = await getScheduleNo(JSON.parse(sessionStorage.getItem("coupon")));
    if (couponNo) {
      await registerCoupon(couponNo, member.mberNo);
    }

    await getSchedulerSetting(member);
  };

  // 일정번호 복호화
  const getScheduleNo = async (noValue) => {
    if (!noValue) return null;

    const url = "/etc/decrypt";
    const body = { encrypted: noValue };

    try {
      const res = await axios.post(url, body);
      if (res.status === 200) {
        return res.data.decrypted;
      }
    } catch (error) {
      return null;
    }
  };

  // 일정 조회
  const getEvent = async (scheduleNo, mber) => {
    const url = "/api/scheduleList";
    const body = { scheduleNo };

    try {
      const res = await axios.post(url, body);

      if (res.status !== 200 || !res.data.length) {
        return;
      }

      const event = res.data[0];

      if (event.mberNo.toString() !== mber.mberNo.toString()) {
        const body = {
          ...event,
          managerNo: event.managerNo ? event.managerNo : event.mberNo,
          workerNos: event.workerList
            ? [...Object.keys(event.workerList), mber.mberNo]
            : [mber.mberNo],
          createMberId: mber.userName,
          updateMberId: mber.userName,
        };
        shareSchedule(body);
      }
    } catch (error) {
      console.error("일정 조회 오류:", error);
    }
  };

  // 일정 공유
  const shareSchedule = async (data) => {
    const url = "/api/schedule";
    const body = {
      ...data,
      delYn: "N",
    };

    try {
      await axios.put(url, body);
    } catch (error) {
      console.log(error);
    }
  };

  // 쿠폰 등록
  const registerCoupon = async (couponCode, mberNo) => {
    const url = "/erp/registerUserCoupon";
    const body = { code: couponCode, mberNo: mberNo };

    try {
      await axios.post(url, body);
    } catch (error) {
      const message = error?.response?.data?.error?.[0]?.message || "잘못된 쿠폰 번호입니다.";
      toast.error(message, { toastId: "no-access" });
    }
  };

  return (
    <LoginFormContainer>
      <Message url={url} homepageInfo={homepageInfo} />
      <form onSubmit={handleSubmit}>
        <label htmlFor="userId" className="label">
          아이디
        </label>
        <input
          type="text"
          id="userId"
          name="userId"
          value={loginInfo.userId}
          onChange={(e) => setLoginInfo((data) => ({ ...data, userId: e.target.value }))}
        />
        <label htmlFor="member_pw" className="label">
          비밀번호
        </label>
        <input
          type="password"
          id="password"
          name="password"
          value={loginInfo.password}
          onChange={(e) => setLoginInfo((data) => ({ ...data, password: e.target.value }))}
        />
        <div className="actions">
          <div className="options">
            {!isMobile && (
              <label>
                <input
                  type="checkbox"
                  name="rememberId"
                  checked={rememberId}
                  onChange={(e) => setRememberId(e.target.checked)}
                />
                <span className="checkmark"></span>
                <span>아이디 저장</span>
              </label>
            )}
            <Link to="/memberSearch">아이디·비밀번호 찾기</Link>
          </div>
          <button type="submit">로그인</button>
          <div className="line">
            <span></span>
            <span>또는</span>
            <span></span>
          </div>
          <button type="button" onClick={loginWithKakao} className="kakao">
            <span>카카오톡 로그인</span>
          </button>
          <div className="join">
            <span>아직 모노티 회원이 아니시라면</span>
            <Link to="/type">회원가입</Link>
          </div>
        </div>
      </form>
    </LoginFormContainer>
  );
}
